import React, { useRef } from "react";

import { Select } from "antd";
import type { SelectProps } from "antd";
import classNames from "classnames";

function removeCO2Options(products) {
  return products.map((category) => ({
    ...category,
    options: category.options.filter(
      (option) => !option.value.includes("CO₂") && !option.label.includes("CO₂")
    )
  }));
}

const subscriptMap: Record<string, string> = {
  "₀": "0",
  "₁": "1",
  "₂": "2",
  "₃": "3",
  "₄": "4",
  "₅": "5",
  "₆": "6",
  "₇": "7",
  "₈": "8",
  "₉": "9"
};

/**
 * Normalizes subscript characters in a string by replacing them with their numeric equivalents.
 *
 * @param {string} str - The input string that may contain subscript characters.
 * @returns {string} - The normalized string with subscript characters replaced by their numeric equivalents.
 */
function normalizeSubscripts(str: string): string {
  return str.replace(/[₀₁₂₃₄₅₆₇₈₉]/g, (char) => subscriptMap[char] || char);
}

const filterOption: SelectProps["filterOption"] = (input, option) => {
  const label =
    typeof option?.label === "string"
      ? option.label
      : typeof option?.label === "number"
      ? option.label.toString()
      : "";
  const normalizedInput = normalizeSubscripts(input.toLowerCase());
  const normalizedLabel = normalizeSubscripts(label.toLowerCase());
  return normalizedLabel.includes(normalizedInput);
};

interface ProductOption {
  readonly value?: string;
  readonly label: string;
  readonly options?: ProductOption[];
}

interface ProductSelectProps {
  readonly selectedProduct: Maybe<string>;
  readonly productOptions: ProductOption[];
  readonly onProductChange: (value: string) => void;
  readonly hideCO2Options?: boolean;
  readonly isFocused?: boolean;
  readonly onSelectRef?: (index: number, selectEl: Maybe<HTMLElement>) => void;
  readonly componentIndex?: number;
}

export default function ProductSelect({
  selectedProduct,
  productOptions,
  onProductChange,
  hideCO2Options = false,
  isFocused = false,
  onSelectRef,
  componentIndex = 0
}: ProductSelectProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);

  return (
    <Select
      ref={(el) => {
        selectRef.current = el;
        if (onSelectRef) onSelectRef(componentIndex, el as HTMLElement);
      }}
      className={classNames("toolbar-item", {
        "keyboard-focused": isFocused
      })}
      data-testid="product-select"
      value={selectedProduct}
      onChange={(value) => {
        onProductChange(value);
        // Blur the select after selection
        if (selectRef.current) {
          selectRef.current.blur();
        }
      }}
      showSearch={true}
      filterOption={filterOption}
      options={hideCO2Options ? removeCO2Options(productOptions) : productOptions}
      popupClassName="modal-select"
    />
  );
}
